import type { ImportReport, ImportConflict } from '@/api/types/core/schemaJson';

// Utilitaires de formatage
export class SchemaImportUtils {
  
  /**
   * Formate la taille d'un fichier en format lisible
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Formate une date ISO en format français
   */
  static formatDate(isoString: string): string {
    return new Date(isoString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Obtient le texte de résolution d'un conflit
   */
  static getConflictResolutionText(conflict: ImportConflict): string {
    switch (conflict.resolution) {
      case 'keep_existing': return 'Conservé existant';
      case 'use_imported': return 'Utilisé importé';
      case 'merge': return 'Fusionné';
      case 'custom': return 'Personnalisé';
      default: return 'Résolu';
    }
  }

  /**
   * Obtient le label d'un type de conflit
   */
  static getConflictTypeLabel(type: string): string {
    switch (type) {
      case 'section_property': return 'Propriété de section';
      case 'translation': return 'Traduction';
      case 'condition': return 'Condition';
      default: return type;
    }
  }

  /**
   * Calcule la similarité entre deux chaînes de caractères
   */
  static calculateStringSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const editDistance = this.levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  /**
   * Distance de Levenshtein pour calculer la similarité
   */
  static levenshteinDistance(str1: string, str2: string): number {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  }

  /**
   * Valide le format d'un fichier JSON de schéma
   */
  static validateSchemaFile(file: File): { valid: boolean; error?: string } {
    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      return { valid: false, error: 'Veuillez sélectionner un fichier JSON valide.' };
    }
    
    if (file.size > 10 * 1024 * 1024) { // 10MB max
      return { valid: false, error: 'Le fichier est trop volumineux (max 10MB).' };
    }
    
    return { valid: true };
  }

  /**
   * Analyse la différence entre deux tableaux de sections
   */
  static analyzeSectionChanges(existingSections: unknown[], importedSections: unknown[]): {
    sectionsToAdd: unknown[];
    sectionsToRemove: unknown[];
    sectionsUnchanged: unknown[];
  } {
    const sectionsToAdd = importedSections.filter(imported => 
      !existingSections.some(existing => (existing as { name: string }).name === (imported as { name: string }).name)
    );
    
    const sectionsToRemove = existingSections.filter(existing => 
      !importedSections.some(imported => (imported as { name: string }).name === (existing as { name: string }).name)
    );
    
    const sectionsUnchanged = existingSections.filter(existing => 
      importedSections.some(imported => (imported as { name: string }).name === (existing as { name: string }).name)
    );
    
    return { sectionsToAdd, sectionsToRemove, sectionsUnchanged };
  }

  /**
   * Analyse la différence entre deux tableaux de traductions
   */
  static analyzeTranslationChanges(existingTranslations: unknown[], importedTranslations: unknown[]): {
    translationsToAdd: unknown[];
    translationsToUpdate: Array<{ existing: unknown; imported: unknown }>;
    translationsToRemove: unknown[];
  } {
    const translationsToAdd: unknown[] = [];
    const translationsToUpdate: Array<{ existing: unknown; imported: unknown }> = [];
    const translationsToRemove = existingTranslations.filter(existing => 
      !importedTranslations.some(imported => 
        (imported as { language_code: string }).language_code === (existing as { language_code: string }).language_code
      )
    );
    
    importedTranslations.forEach(imported => {
      const existing = existingTranslations.find(e => 
        (e as { language_code: string }).language_code === (imported as { language_code: string }).language_code
      );
      if (existing) {
        // Vérifier si le contenu a changé
        const existingTyped = existing as { titre: string; description: string };
        const importedTyped = imported as { titre: string; description: string };
        if (existingTyped.titre !== importedTyped.titre || existingTyped.description !== importedTyped.description) {
          translationsToUpdate.push({ existing, imported });
        }
      } else {
        translationsToAdd.push(imported);
      }
    });
    
    return { translationsToAdd, translationsToUpdate, translationsToRemove };
  }

  /**
   * Collecte tous les parents d'une section de manière récursive
   */
  static collectRequiredParents(sectionName: string, getParentName: (name: string) => string | null): string[] {
    const parents: string[] = [];
    let currentParent = getParentName(sectionName);
    
    while (currentParent) {
      parents.unshift(currentParent); // Ajouter au début pour avoir l'ordre hiérarchique
      currentParent = getParentName(currentParent);
    }
    
    return parents;
  }

  /**
   * Génère un résumé du rapport d'import
   */
  static generateImportSummary(report: ImportReport): string {
    const parts: string[] = [];
    
    if (report.sections_created > 0) {
      parts.push(`${report.sections_created} section(s) créée(s)`);
    }
    
    if (report.sections_updated > 0) {
      parts.push(`${report.sections_updated} section(s) mise(s) à jour`);
    }
    
    if (report.translations_created > 0) {
      parts.push(`${report.translations_created} traduction(s) créée(s)`);
    }
    
    if (report.translations_updated > 0) {
      parts.push(`${report.translations_updated} traduction(s) mise(s) à jour`);
    }
    
    if (report.conditions_created > 0) {
      parts.push(`${report.conditions_created} condition(s) créée(s)`);
    }
    
    if (report.conflicts.length > 0) {
      parts.push(`${report.conflicts.length} conflit(s) résolu(s)`);
    }
    
    return parts.length > 0 ? parts.join(', ') : 'Aucune modification';
  }

  /**
   * Vérifie si un objet a une structure de schéma valide
   */
  static isValidSchemaStructure(data: unknown): boolean {
    if (!data || typeof data !== 'object') return false;
    
    const obj = data as Record<string, unknown>;
    return (
      typeof obj.appellation === 'string' &&
      typeof obj.type === 'string' &&
      Array.isArray(obj.section_list)
    );
  }

  /**
   * Nettoie et normalise les données d'un schéma importé
   */
  static normalizeImportedSchema(rawSchema: unknown): unknown {
    const schema = rawSchema as Record<string, unknown>;
    return {
      ...schema,
      appellation: ((schema.appellation as string) || '').trim(),
      type: ((schema.type as string) || '').trim(),
      description: ((schema.description as string) || '').trim(),
      section_list: Array.isArray(schema.section_list) ? schema.section_list : [],
      version: schema.version || new Date().toISOString()
    };
  }

  /**
   * Génère les avertissements pour une action d'alignement
   */
  static generateAlignmentWarnings(
    sectionId: string, 
    targetSection: string, 
    allSections: Array<{ id: string }>
  ): string[] {
    const warnings: string[] = [];
    
    // Vérifier si la section importée a des enfants qui ne seront pas importés
    const childrenInSchema = allSections.filter(s => 
      s.id.startsWith(sectionId + '.')
    );
    
    if (childrenInSchema.length > 0) {
      warnings.push(
        `${childrenInSchema.length} section(s) enfant(s) ne seront pas importées : ${
          childrenInSchema.map(s => s.id.split('.').pop()).join(', ')
        }`
      );
    }
    
    // Vérifier si on aligne une section container vers une section finale
    if (childrenInSchema.length > 0) {
      warnings.push(
        `Attention : aligner une section avec enfants vers "${targetSection}" peut causer des conflits`
      );
    }
    
    return warnings;
  }
} 